import * as com from "../Common.js"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { debounce } from "lodash"
import {
  Card,
  Switch,
  Form,
  Input,
  DatePicker,
  Select,
  Row,
  Col,
  Typography,
  InputNumber,
  Spin,
  message,
} from "antd"
import "./Escrow.css"
import { CurrentStatus } from "../Common"
import { AddressComponent } from "Common/AddressAutocomplete"
import dayjs from "dayjs"
import RuleWarning from "Common/components/RuleWarning"
import { UpdateApplicationAttribute } from "../Store/actions"
import { checkAccess } from "../Auth"
import { UR_Owner } from "../State"
import { closeOfEscrowFlag } from "Common/utils/closeOfEscrow"
import {
  getLosEscrowV2,
  patchLosEscrowV2,
  getLosApplicationV2,
} from "../DocumentChecklist/applicationV2Client"

/** Unknown/empty status sorts below ApplicationStarted so OpenEscrow can still bump. */
const statusRank = (status?: string) => CurrentStatus[status || ""] ?? 0

const { Text } = Typography
const { Option } = Select

/**
 * Writes the close of escrow date (MM/DD/YYYY, "" clears it) through the
 * pipeline endpoint, which keeps the old loan state and loan_applications in
 * step. Not part of the escrow PATCH, which only writes the relational model.
 */
const saveCloseOfEscrowDate = async (loanId: string, value: string) => {
  const response = await fetch("/los/updatepipeline", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + sessionStorage.getItem("ZeitroA"),
      Cache: "no-cache",
    },
    body: JSON.stringify({ LoanID: loanId, Key: "closingdate", Value: value, IsAdmin: !!checkAccess([UR_Owner]) }),
  })
  if (!response.ok) {
    const text = (await response.text()).trim()
    throw new Error(text || `Failed to save the close of escrow date (${response.status})`)
  }
}

const emptyEscrow = () => ({
  escrowopened: false,
  opendate: "",
  estimatedsettlementstatementreceived: false,
  titlecompany: "",
  escrowofficer: "",
  email: "",
  phone: "",
  escrownumberrelatedcurrentfile: "",
  datetitlesearchordered: "",
  titlecompanyaddress1: "",
  titlecompanyaddress2: "",
  titlecompanycity: "",
  titlecompanystate: "",
  titlecompanyzip: "",
  titlecompanycounty: "",
  earnestmoneydepositamount: "",
  earnestmoneydepositdate: "",
})

type EscrowV2Props = {
  loanId: string
}

export const EscrowV2 = ({ loanId }: EscrowV2Props) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [escrowInfo, setEscrowInfo] = useState(emptyEscrow())
  const [currentStage, setCurrentStage] = useState("")
  const [currentStatus, setCurrentStatus] = useState("")
  const [closingDate, setClosingDate] = useState("")
  const escrowRef = useRef(escrowInfo)
  const pipelineRef = useRef({ currentStage: "", currentStatus: "" })

  useEffect(() => {
    escrowRef.current = escrowInfo
  }, [escrowInfo])

  useEffect(() => {
    pipelineRef.current = { currentStage, currentStatus }
  }, [currentStage, currentStatus])

  const persist = useMemo(
    () =>
      debounce(async (nextEscrow, pipeline?: { currentStage?: string; currentStatus?: string }) => {
        if (!loanId) return
        try {
          const payload: any = { escrowInfo: nextEscrow }
          // Only write pipeline when explicitly bumped (opening escrow), not on every field edit.
          if (pipeline?.currentStage) payload.currentStage = pipeline.currentStage
          if (pipeline?.currentStatus) payload.currentStatus = pipeline.currentStatus
          await patchLosEscrowV2(loanId, payload)
        } catch (err: any) {
          console.error(err)
          message.error(err?.message || "Failed to save escrow")
        }
      }, 600),
    [loanId]
  )

  useEffect(() => () => {
    persist.flush()
    persist.cancel()
  }, [persist])

  useEffect(() => {
    if (!loanId) return undefined
    let cancelled = false
    setLoading(true)
    Promise.all([getLosEscrowV2(loanId), getLosApplicationV2(loanId)])
      .then(([escrowBody, appBody]) => {
        if (cancelled) return
        setEscrowInfo({ ...emptyEscrow(), ...(escrowBody.escrowInfo || {}) })
        const app = appBody.Application || {}
        setCurrentStage(app.currentStage || "")
        setCurrentStatus(app.currentStatus || "")
        setClosingDate(app.closingdate || "")
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err)
          message.error(err?.message || "Failed to load escrow")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [loanId])

  const saveField = useCallback(
    (value: any, key: string) => {
      // AddressComponent may still fire per-field callbacks on manual edits.
      // Keep the ref current so rapid sequential updates do not clobber each other.
      const next = { ...escrowRef.current, [key]: value }
      escrowRef.current = next
      setEscrowInfo(next)
      persist(next)
    },
    [persist]
  )

  const saveFields = useCallback(
    (partial: Record<string, any>) => {
      const next = { ...escrowRef.current, ...partial }
      escrowRef.current = next
      setEscrowInfo(next)
      persist(next)
    },
    [persist]
  )

  const onClosingDateChange = async (value: string) => {
    const previous = closingDate
    setClosingDate(value)
    try {
      await saveCloseOfEscrowDate(loanId, value)
      // Pages that still save the whole loan state send this value back.
      dispatch(UpdateApplicationAttribute(value, "closingdate"))
    } catch (err: any) {
      console.error(err)
      setClosingDate(previous)
      message.error(err?.message || "Failed to save the close of escrow date")
    }
  }

  const closingFlag = closeOfEscrowFlag(closingDate, {
    finished: currentStage === "Disposed" || statusRank(currentStatus) >= CurrentStatus["LoanFunded"],
  })

  const onToggleOpened = (checked: boolean) => {
    let pipeline: { currentStage?: string; currentStatus?: string } | undefined
    if (checked && statusRank(pipelineRef.current.currentStatus) < CurrentStatus["OpenEscrow"]) {
      pipeline = { currentStage: "Processing", currentStatus: "OpenEscrow" }
      setCurrentStage(pipeline.currentStage!)
      setCurrentStatus(pipeline.currentStatus!)
      // Header StageStatusForm reads Redux; persist still goes to V2 split tables only.
      dispatch(UpdateApplicationAttribute(pipeline.currentStage, "currentStage"))
      dispatch(UpdateApplicationAttribute(pipeline.currentStatus, "currentStatus"))
    }
    const next = { ...escrowRef.current, escrowopened: checked }
    escrowRef.current = next
    setEscrowInfo(next)
    persist(next, pipeline)
  }

  if (loading) {
    return (
      <div className="escrow-container" style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    )
  }

  return (
    <div className="escrow-container">
      <Card className="escrow-card">
        <div className="escrow-header">
          <div className="escrow-title">Escrow Information</div>
          <div className="escrow-toggle-container">
            <Switch
              className="escrow-switch"
              checked={!!escrowInfo.escrowopened}
              onChange={onToggleOpened}
            />
            <div className="escrow-toggle-label">
              {escrowInfo.escrowopened ? "Escrow opened" : "Escrow not opened"}
            </div>
          </div>
        </div>

        <Text type="secondary" className="escrow-description">
          For <b>record-keeping</b> only. Borrowers will only see the updated status, not the detailed information.
        </Text>

        <Form layout="vertical" className="escrow-form">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Close of escrow date"
                extra={closingFlag ? (
                  <span className={`escrow-closing-flag escrow-closing-flag-${closingFlag.kind}`}>{closingFlag.label}</span>
                ) : undefined}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Choose date"
                  format="MM/DD/YYYY"
                  value={closingDate && dayjs(closingDate, "MM/DD/YYYY").isValid() ? dayjs(closingDate, "MM/DD/YYYY") : null}
                  onChange={(date) => onClosingDateChange(date ? date.format("MM/DD/YYYY") : "")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {escrowInfo.escrowopened && (
          <Form layout="vertical" className="escrow-form">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Open date">
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Choose date"
                    value={escrowInfo.opendate ? dayjs(escrowInfo.opendate, "MM/DD/YYYY") : null}
                    onChange={(date) => saveField(date ? date.format("MM/DD/YYYY") : "", "opendate")}
                    format="MM/DD/YYYY"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Estimated settlement statement received?">
                  <Select
                    placeholder="-"
                    value={escrowInfo.estimatedsettlementstatementreceived}
                    onChange={(value) => saveField(value, "estimatedsettlementstatementreceived")}
                  >
                    <Option value={true}>Yes</Option>
                    <Option value={false}>No</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Title company">
                  <Input
                    placeholder="Enter company name"
                    value={escrowInfo.titlecompany}
                    onChange={(e) => saveField(e.target.value, "titlecompany")}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Escrow officer">
                  <Input
                    placeholder="Enter escrow officer name"
                    value={escrowInfo.escrowofficer}
                    onChange={(e) => saveField(e.target.value, "escrowofficer")}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Email">
                  <RuleWarning warning={!/^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/.test(escrowInfo.email || "")}>
                    <Input
                      type="email"
                      placeholder="Contact email address"
                      value={escrowInfo.email}
                      onChange={(e) => saveField(e.target.value, "email")}
                    />
                  </RuleWarning>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Phone number">
                  <RuleWarning warning={!/^(?:\+1\s?)?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{4}$|^$/.test(escrowInfo.phone || "")}>
                    <Input
                      placeholder="Contact phone number"
                      value={com.fixPhoneInput(escrowInfo.phone)}
                      onChange={(e) => saveField(com.fixPhoneInput(e.target.value), "phone")}
                    />
                  </RuleWarning>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Escrow number">
                  <Input
                    placeholder="Enter escrow number"
                    value={escrowInfo.escrownumberrelatedcurrentfile}
                    onChange={(e) => saveField(e.target.value, "escrownumberrelatedcurrentfile")}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Date title search ordered">
                  <DatePicker
                    format="MM/DD/YYYY"
                    style={{ width: "100%" }}
                    placeholder="Choose date"
                    value={escrowInfo.datetitlesearchordered ? dayjs(escrowInfo.datetitlesearchordered, "MM/DD/YYYY") : null}
                    onChange={(date) => saveField(date ? date.format("MM/DD/YYYY") : "", "datetitlesearchordered")}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <AddressComponent
                  id="escrowTitleAddressV2"
                  label="Title company address"
                  address={escrowInfo.titlecompanyaddress1}
                  state={escrowInfo.titlecompanystate}
                  county={escrowInfo.titlecompanycounty}
                  city={escrowInfo.titlecompanycity}
                  zip={escrowInfo.titlecompanyzip}
                  onAddressLineChange={(value) => saveField(value, "titlecompanyaddress1")}
                  onStateChange={(value) => saveField(value, "titlecompanystate")}
                  onCountyChange={(value) => saveField(value, "titlecompanycounty")}
                  onCityChange={(value) => saveField(value, "titlecompanycity")}
                  onZipChange={(value) => saveField(value, "titlecompanyzip")}
                  onPlaceFields={({ address, state, county, city, zip }) => saveFields({
                    titlecompanyaddress1: address || "",
                    titlecompanystate: state || "",
                    titlecompanycounty: county || "",
                    titlecompanycity: city || "",
                    titlecompanyzip: zip || "",
                  })}
                  placeholder="Address line"
                  cityPlaceholder="City"
                  zipPlaceholder="Zip code"
                  wrapperClassName="esrow-wrapper"
                />
              </Col>

              <Col span={12}>
                <Form.Item label="Date Earnest Money Deposit received">
                  <DatePicker
                    style={{ width: "100%" }}
                    placeholder="Choose date"
                    value={escrowInfo.earnestmoneydepositdate ? dayjs(escrowInfo.earnestmoneydepositdate, "MM/DD/YYYY") : null}
                    onChange={(date) => saveField(date ? date.format("MM/DD/YYYY") : "", "earnestmoneydepositdate")}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label="Earnest Money Deposit amount">
                  <InputNumber
                    className="escrow-money-input"
                    style={{ width: "100%" }}
                    placeholder="0"
                    min={0}
                    precision={2}
                    prefix="$"
                    value={escrowInfo.earnestmoneydepositamount ? parseFloat(escrowInfo.earnestmoneydepositamount) : null}
                    onChange={(value) => saveField(value?.toString() || "", "earnestmoneydepositamount")}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Card>
    </div>
  )
}

export default EscrowV2
